import { ref, computed } from 'vue';

/**
 * Get auth token from localStorage
 * Unified helper to avoid inconsistent token access
 * @returns {string|null} JWT token or dev bypass token
 */
export function getAuthToken() {
  // Dev bypass mode
  const isDevBypass = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';
  if (isDevBypass) {
    return 'dev-token-bypass';
  }

  // Normal mode - read from userAuth
  const storedAuth = localStorage.getItem('userAuth');
  if (storedAuth) {
    try {
      const authData = JSON.parse(storedAuth);
      if (authData.expiresAt && new Date(authData.expiresAt) > new Date()) {
        return authData.token;
      }
    } catch {
      // Invalid JSON, ignore
    }
  }

  return null;
}

export function useAuth() {
  const currentUser = ref(null);
  const authToken = ref(null);
  const isAuthenticated = computed(() => currentUser.value && authToken.value);

  const checkAuthStatus = () => {
    const token = getAuthToken();
    if (token && token !== 'dev-token-bypass') {
      const authData = JSON.parse(localStorage.getItem("userAuth"));
      currentUser.value = authData.user;
      authToken.value = token;
    } else if (token === 'dev-token-bypass') {
      currentUser.value = { email: 'dev@localhost' };
      authToken.value = token;
    } else {
      currentUser.value = null;
      authToken.value = null;
    }
  };

  const handleLoginSuccess = async (user, onSuccess) => {
    const authData = JSON.parse(localStorage.getItem("userAuth"));
    currentUser.value = user;
    authToken.value = authData.token;

    if (onSuccess) {
      await onSuccess();
    }
  };

  const handleLogout = (onLogout) => {
    currentUser.value = null;
    authToken.value = null;
    localStorage.removeItem("userAuth");
    localStorage.removeItem("chatSessionId");

    if (onLogout) {
      onLogout();
    }
  };

  return {
    currentUser,
    authToken,
    isAuthenticated,
    checkAuthStatus,
    handleLoginSuccess,
    handleLogout,
  };
}
